<template>
  <!-- The tenant's accent is applied as an INLINE override of the accent tokens
       on this one element. A custom property only inherits downwards, so the
       repaint reaches exactly this screen's subtree — the sign-in button, the
       focus rings, the links — and nothing else: the moment the user is signed
       in and the workspace chrome renders, the suite's own tokens are back.
       That is the whole point of not touching the global stylesheet here. -->
  <div
    class="min-h-screen flex flex-col items-center px-4 pt-6 relative overflow-hidden bg-surface-canvas"
    :style="brandStyle"
    data-testid="login-screen"
  >
    <!-- The tenant's backdrop. An <img> rather than a CSS background so a URL
         that 404s can say so (`@error`) and fall back to the plain surface
         instead of leaving a dead grey rectangle. Decorative: the login form,
         not the picture, is the content. -->
    <img
      v-if="heroUrl"
      :src="heroUrl"
      alt=""
      aria-hidden="true"
      class="pointer-events-none absolute inset-0 w-full h-full object-cover"
      data-testid="login-hero"
      @error="heroFailed = true"
    />
    <!-- Scrim: the card and the Lumi mark have to stay readable over a picture
         nobody here has seen. -->
    <div v-if="heroUrl" aria-hidden="true" class="pointer-events-none absolute inset-0 bg-[var(--overlay)]"></div>

    <!-- `my-auto` centres the card in the room the Lumi mark leaves below it:
         the mark sits at the foot of the screen, in flow, so the two can never
         overlap however short the viewport (#465). -->
    <Card :padded="false" class="login-card my-auto max-w-md w-full relative z-10 shadow-lg">
      <div class="text-center mb-8">
        <img
          v-if="tenantLogoUrl"
          :src="tenantLogoUrl"
          :alt="t('login.tenantLogoAlt')"
          class="h-10 mx-auto mb-5 object-contain"
          data-testid="login-tenant-logo"
          @error="logoFailed = true"
        />
        <img v-else src="@/assets/lumi-hr-logo.svg" alt="lumi-HR" class="brand-logo h-10 mx-auto mb-5" data-testid="login-default-logo" />
        <!-- The workspace's own name, not the product's (#416). Falls back to the
             suite name whenever user-api could not name a tenant — a bare/dev
             host, an unknown slug, a degraded /auth/methods — so the screen is
             never headless. A tenant that chose a primary colour sees its name
             in it (#465) — `--accent` carries that colour on this subtree. -->
        <h1
          class="text-2xl font-bold mb-1"
          :class="branding.primaryColor ? 'text-accent' : 'text-title'"
          data-testid="login-title"
        >
          {{ tenantName || t('login.title') }}
        </h1>
        <p class="text-body text-sm">{{ t('login.subtitle') }}</p>
      </div>

      <InlineNotice v-if="error || formError" variant="error" class="mb-5">
        {{ error || formError }}
      </InlineNotice>

      <!-- An idle sign-out is not a failure, so it is a status and not an alert:
           the same red box would read as "something went wrong" for a routine
           security measure the user's own inactivity triggered (issue 265). -->
      <div v-if="notice" role="status" data-testid="login-notice" class="p-3 bg-info-soft border border-info-soft rounded-lg mb-5">
        <p class="text-sm text-info-text">{{ notice }}</p>
      </div>

      <!-- A system admin's second factor (issue 619, ADR 0094): an operator signing in
           here under a support grant gets this step after the password. Setting
           the authenticator up happens on the console, never on a workspace. -->
      <form v-if="mfaChallenge" class="space-y-5" data-testid="login-mfa" @submit.prevent="submitMfaCode">
        <div class="space-y-1">
          <h2 class="text-base font-semibold text-heading">{{ t('login.mfaTitle') }}</h2>
          <p class="text-sm text-body">{{ t('login.mfaIntro') }}</p>
        </div>
        <Field :label="t('login.mfaCode')" class="login-field">
          <Input v-model="mfaCode" class="login-input" autocomplete="one-time-code" maxlength="11" data-testid="login-mfa-code" />
        </Field>
        <Button type="submit" block :disabled="mfaPending || !mfaCode.trim()" data-testid="login-mfa-submit">
          {{ mfaPending ? t('login.signingIn') : t('login.mfaSubmit') }}
        </Button>
        <Button variant="ghost" block @click="leaveMfa('')">
          {{ t('login.mfaBack') }}
        </Button>
      </form>

      <!-- A real element rather than a template fragment, so swapping this block
           for the code step unmounts one node: the console's MFA step (issue 619)
           failed in Vue's fragment removal on exactly this shape, a fragment
           holding fragments. -->
      <div v-else>
      <!-- Passkey (#387). Offered ABOVE the SSO buttons and the password form
           because it is the fastest way in for someone who has one, and hidden
           entirely otherwise — the server decides whether this host may offer it
           at all (a custom domain and an enforce-SSO workspace both cannot). -->
      <div v-if="passkeyOffered" class="mb-5">
        <Button variant="secondary" block :disabled="passkeyPending" @click="signInWithPasskey">
          {{ passkeyPending ? t('login.passkeySigningIn') : t('login.passkeySignIn') }}
        </Button>
        <InlineNotice v-if="passkeyError" variant="error" class="mt-2">{{ passkeyError }}</InlineNotice>
      </div>

      <!-- SSO provider buttons: top-level navigation to the RP start endpoint. -->
      <div v-if="ssoMethods.length" class="space-y-3 mb-5">
        <a
          v-for="m in ssoMethods"
          :key="m.key"
          :href="ssoStartUrl(m.key)"
          class="flex items-center justify-center w-full px-4 py-2.5 border border-strong rounded-md text-sm font-medium text-heading hover:bg-surface-wash"
        >
          {{ ssoLabel(m) }}
        </a>
      </div>

      <p v-if="!passwordEnabled && !showPasswordForm" class="text-sm text-secondary text-center">
        {{ t('login.ssoOnly') }}
        <Button variant="secondary" block class="mt-3" @click="showPasswordForm = true">
          {{ t('login.adminLogin') }}
        </Button>
      </p>

      <template v-if="showPasswordForm">
        <div v-if="ssoMethods.length" class="relative my-5 text-center">
          <span class="px-2 bg-surface text-xs text-tertiary relative z-10">{{ t('login.orContinueWith') }}</span>
          <div class="absolute inset-x-0 top-1/2 border-t border-default"></div>
        </div>

        <form @submit.prevent="handleLogin" class="space-y-5">
          <Field for="email" :label="t('login.email')" class="login-field">
            <!-- `webauthn` in autocomplete is what lets the browser offer a passkey
                 INSIDE this field (conditional mediation): Apple Passwords, Google
                 Password Manager and Windows Hello all key their autofill prompt
                 on that token, and without it the conditional ceremony started
                 below runs but never surfaces anywhere. `username` rather than
                 `email` is the value the WebAuthn autofill spec and every
                 browser pair it with. -->
            <Input
              id="email"
              class="login-input"
              v-model="email"
              type="email"
              required
              autocomplete="username webauthn"
              :placeholder="t('login.emailPlaceholder')"
            />
          </Field>

          <Field for="password" :label="t('login.password')" class="login-field">
            <Input
              id="password"
              class="login-input"
              v-model="password"
              type="password"
              required
              autocomplete="current-password"
              :placeholder="t('login.passwordPlaceholder')"
            />
          </Field>

          <Button type="submit" block :disabled="loading">
            {{ loading ? t('login.signingIn') : t('login.signIn') }}
          </Button>
        </form>

        <div class="mt-4 text-center">
          <RouterLink to="/login/forgot" class="text-sm text-accent hover:underline">{{ t('login.forgotPassword') }}</RouterLink>
        </div>
      </template>

      <!-- "Lost your phone?" (issue 698, ADR 0093). Outside the password form: a
           phone-only member has no password, and a workspace may have the form
           switched off. Shown only where the server says this workspace can send a
           recovery link at all — a link to a door that never opens is worse than
           none. -->
      <div v-if="phoneRecovery" class="mt-4 text-center">
        <RouterLink to="/login/recover" class="text-sm text-accent hover:underline" data-testid="login-phone-recovery">
          {{ t('login.lostPhone') }}
        </RouterLink>
      </div>
      </div>

      <div class="mt-6 flex items-center justify-center gap-2">
        <LumiIcon name="language" class="w-4 h-4 text-tertiary" />
        <Select
          :model-value="locale"
          size="sm"
          class="login-locale"
          :options="localeOptions.map((l) => ({ value: l.code, label: l.label }))"
          @update:model-value="onLocaleSelect"
        />
      </div>

      <!-- Issue 576: shown only once the public read has succeeded (see the
           probe above) — a link that could 404 must never render. -->
      <div v-if="employeeNoticeHref" class="mt-3 text-center">
        <RouterLink
          :to="employeeNoticeHref"
          class="text-xs text-secondary hover:text-accent hover:underline"
          data-testid="login-employee-notice-link"
        >{{ t('legal.employeeNotice', { company: tenantName || t('login.title') }) }}</RouterLink>
      </div>
    </Card>

    <!-- Always rendered, branded or not: whose product this is stays visible on
         every tenant's front door. It sits at the foot of the screen on the
         login card's own frosted surface (#465) — the blur keeps it legible
         over a backdrop nobody here has seen, as it does the form. `relative` is
         load-bearing (#473): the hero and its scrim are absolutely positioned,
         and positioned boxes paint above in-flow ones whatever the DOM order,
         so a static pill sat underneath any tenant's backdrop. Once positioned,
         DOM order does decide — it is the last element, so it paints above the
         hero/scrim with no z-index (check-raw-layer-usage). -->
    <div class="login-mark relative mt-6 mb-6 flex flex-col items-center gap-2 px-4 py-3 shadow-sm" data-testid="login-lumi-mark">
      <span class="text-base text-secondary">{{ t('login.poweredBy') }}</span>
      <img src="@/assets/lumi-hr-logo.svg" alt="lumi-HR" class="brand-logo h-10" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { Button, Card, Field, Input, Select, InlineNotice, LumiIcon } from '@lumi/ui';
import { IDLE_LOGOUT_REASON, IDLE_TIMEOUT_MS, useApiMutation } from '@lumi/app-kit';
import { useAuthStore } from '@/stores/auth';
import { userApi } from '@/utils/api';
import {
  passkeysSupported,
  getPasskeyAssertion,
  conditionalMediationAvailable,
  ceremonyOutcome,
} from '@/utils/passkey';
import { currentTenantSlug, tenantSlugFromWorkspaceHost } from '@/utils/runtime-config';
import { internalReturnPath, isSafeReturnPath, postLoginRedirectUrl } from '@/utils/module-links';
import { fetchPublicEmployeeNotice } from '@/utils/employeeNotice';
import { setLocale, SUPPORTED_LOCALES } from '@/i18n';

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const { t, locale } = useI18n();
const email = ref('');
const password = ref('');
// Failures that are not the sign-in attempt itself (an SSO round-trip that came
// back with an error) — the seam owns the attempt's own message, see `formError`.
const error = ref('');
const notice = ref('');

// Sign-in methods offered by this tenant host (ADR 0024). passwordEnabled=false
// means enforce-SSO; a break-glass toggle still reveals the password form for
// the tenant owner / sysadmins.
const ssoMethods = ref<Array<{ key: string; label?: string }>>([]);
const passwordEnabled = ref(true);
const showPasswordForm = ref(true);
const ssoLabels: Record<string, string> = {
  google: 'ssoGoogle',
  microsoft: 'ssoMicrosoft',
  // Apple's Human Interface Guidelines require the exact words "Sign in with
  // Apple" in each language, and Apple publishes the translations — so this key's
  // value is THEIRS, not ours, and must not be reworded to match house style
  // (#400).
  apple: 'ssoApple',
  enterprise: 'ssoEnterprise',
};

// Passkeys (#387). Two independent conditions, and BOTH have to hold: the server
// says this host may offer them (relying-party scope, enforce-SSO), and the
// browser can actually run a ceremony. Neither implies the other.
const passkeysEnabled = ref(false);
// Whether this workspace can send a passkey-recovery link by SMS (issue 698):
// phone access on, and passkeys allowed on this host. The server decides.
const phoneRecovery = ref(false);
const passkeySupported = ref(false);
const passkeyOffered = computed(() => passkeysEnabled.value && passkeySupported.value);
const passkeyPending = ref(false);
const passkeyError = ref('');
// The conditional (autofill) ceremony runs for the whole time the form is open,
// so it needs a way to be called off — when the person signs in another way, or
// leaves. An aborted conditional ceremony is EXPECTED, never an error.
let conditionalAbort: AbortController | null = null;
// A browser runs ONE WebAuthn request at a time: starting the button ceremony
// while the autofill one is pending is refused outright ("a request is already
// pending"), so the modal ceremony must abort the conditional one first and
// hand the field back to it afterwards. These two guard that hand-over.
let conditionalActive = false;
let unmounted = false;

// The tenant's own login-screen identity, from the SAME pre-auth /auth/methods
// call the SSO buttons come from (user-api reads it by host/slug — there is no
// session yet and never will be one on this screen). Every field is optional and
// independently so: a workspace may set only a logo, only a color, or nothing at
// all, and "nothing at all" has to look finished, not empty — so each field falls
// back on its own to what this screen showed before any of this existed.
type LoginBranding = { logoUrl: string | null; primaryColor: string | null; imageUrl: string | null };
const branding = ref<LoginBranding>({ logoUrl: null, primaryColor: null, imageUrl: null });
// The workspace's display name from the same response (#416). Read as defensively
// as the branding fields below it: anything that is not a non-empty string leaves
// the heading on the suite's own product name.
const tenantName = ref<string | null>(null);
// An asset the tenant configured but the browser cannot load (moved, 404, blocked)
// is not a reason to show a hole: we fall back exactly as if it were unset.
const logoFailed = ref(false);
const heroFailed = ref(false);
// The employee-notice link below the form (issue 576). Set only once the
// public read has actually succeeded — never a broken link — so its presence
// alone is the flag; no separate "shown" boolean to drift out of sync with it.
const employeeNoticeSlug = ref('');
const employeeNoticeHref = computed(() =>
  employeeNoticeSlug.value
    ? `/legal/employee-privacy?tenantSlug=${encodeURIComponent(employeeNoticeSlug.value)}`
    : '',
);

const tenantLogoUrl = computed(() => (logoFailed.value ? null : branding.value.logoUrl));
const heroUrl = computed(() => (heroFailed.value ? null : branding.value.imageUrl));
const brandStyle = computed<Record<string, string>>(() => {
  const style: Record<string, string> = {};
  const color = branding.value.primaryColor;
  if (!color) return style;
  // user-api validated this against a hex/rgb()/hsl() grammar before storing it,
  // and a style OBJECT sets each property individually, so a malformed value can
  // only be dropped by the CSS parser — it cannot open a second declaration.
  for (const token of ['--accent', '--accent-hover', '--accent-text', '--accent-ring']) {
    style[token] = color;
  }
  return style;
});

// Where the SSO round-trip should land after the ticket is redeemed. When a
// module sent us here to log in, return to /login still carrying its
// module/returnPath so the router's post-login redirect bounces us on into that
// module; otherwise the workspace homebase. The path is workspace-origin-local,
// so the backend's return-path allowlist accepts it.
function ssoReturnTo(): string {
  const moduleKey = typeof route.query.module === 'string' ? route.query.module : '';
  const returnPath = typeof route.query.returnPath === 'string' ? route.query.returnPath : '';
  if (!moduleKey) {
    // #445 M2 — a workspace-internal deep link (no module, e.g. a notification
    // mail's /notifications/:id/open) still deserves to survive an SSO round
    // trip: carry its own returnPath along on its own, same slot the router's
    // requiresAuth gate stamped it into.
    return isSafeReturnPath(returnPath) ? `/login?returnPath=${encodeURIComponent(returnPath)}` : '/';
  }
  const params = new URLSearchParams();
  params.set('module', moduleKey);
  if (returnPath) params.set('returnPath', returnPath);
  return `/login?${params.toString()}`;
}

function ssoStartUrl(providerKey: string) {
  return `/api/user/auth/sso/${providerKey}/start?returnTo=${encodeURIComponent(ssoReturnTo())}`;
}
function ssoLabel(m: { key: string; label?: string }) {
  if (m.key === 'enterprise' && m.label) return m.label;
  return t(`login.${ssoLabels[m.key] || 'ssoEnterprise'}`);
}

onMounted(async () => {
  // Every sso_error value reads as one generic "please try again" — which is a
  // lie for exactly one of them (issue 299 D23). user-api refuses a first SSO
  // login when the workspace has no free user seat and hands that over as
  // `seat_limit`; retrying can never succeed, and this viewer is not signed in
  // and holds no role, so there is no seat stepper to send them to. What they get
  // instead is the truth: the people who can book a seat have been told.
  if (route.query.sso_error) {
    // #576: JIT provisioning hands the employee-notice gate's own refusal code
    // through verbatim (never the generic 'error') so this screen can say what
    // is actually true — the workspace itself is not ready, not "SSO broke".
    if (route.query.sso_error === 'seat_limit') {
      error.value = t('login.ssoSeatLimit');
    } else if (route.query.sso_error === 'EMPLOYEE_NOTICE_INCOMPLETE') {
      error.value = t('login.ssoEmployeeNoticeIncomplete');
    } else if (route.query.sso_error === 'LEGAL_READINESS_UNAVAILABLE') {
      error.value = t('login.ssoLegalReadinessUnavailable');
    } else if (route.query.sso_error === 'operator_password_only') {
      // issue 619: a system admin's sign-in carries a second factor no IdP can.
      error.value = t('login.ssoOperatorPasswordOnly');
    } else if (route.query.sso_error === 'link_required') {
      // issue 926: the company sign-in matched an existing account that it may not
      // take over by itself (an address domain the workspace has not proven, or an
      // account that also belongs to other workspaces). Retrying cannot help; the
      // person connects it herself from a session of her own.
      error.value = t('login.ssoLinkRequired');
    } else {
      error.value = t('login.ssoError');
    }
  }
  // Every module UI bounces an idled-out session here with this reason, so one
  // sentence in the shared catalog explains it for the whole suite (issue 265).
  if (route.query.reason === IDLE_LOGOUT_REASON) {
    notice.value = t('session.idleSignedOut', { minutes: Math.round(IDLE_TIMEOUT_MS / 60_000) });
  }
  // The employee-notice link (issue 576): a silent probe, independent of the
  // /auth/methods call below — a degraded user-api must not hide a notice
  // that IS reachable, and this read must never delay or error the sign-in
  // screen it sits alongside.
  void (async () => {
    const slug = currentTenantSlug();
    if (!slug) return;
    try {
      await fetchPublicEmployeeNotice(slug, locale.value === 'en' ? 'en' : 'de');
      employeeNoticeSlug.value = slug;
    } catch {
      /* no notice published, or unreachable — no link, silently */
    }
  })();
  try {
    // `currentTenantSlug`, NOT the host-only resolver the sign-in calls below use.
    // The difference is what the slug is FOR. Here it only decides which name and
    // which logo this screen wears, so the bare-host fallback (`kb_tenant_slug`,
    // the scope the router writes from `?tenantSlug=`) is worth reading: on
    // localhost the host names no tenant, which is why the workspace name and the
    // tenant branding never appeared in local dev (#416). Below, the same string
    // is an AUTHORIZATION input — the server requires membership of the tenant it
    // names — so those calls stay on the hostname, the one source a browser cannot
    // choose for itself.
    const slug = currentTenantSlug();
    const { data } = await userApi.get('/auth/methods', { params: slug ? { tenantSlug: slug } : {} });
    ssoMethods.value = Array.isArray(data.methods) ? data.methods : [];
    passwordEnabled.value = data.password !== false;
    showPasswordForm.value = passwordEnabled.value;
    passkeysEnabled.value = data.passkeys === true;
    phoneRecovery.value = data.phoneRecovery === true;
    passkeySupported.value = passkeysSupported();
    if (passkeyOffered.value) void startConditionalPasskey();
    // Defensive on every field: this response is the first thing an unauthenticated
    // browser touches, and an older/degraded user-api may answer without `branding`
    // at all. Anything that is not a non-empty string stays null → the default screen.
    const raw = (data?.branding ?? {}) as Partial<Record<keyof LoginBranding, unknown>>;
    const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : null);
    branding.value = { logoUrl: str(raw.logoUrl), primaryColor: str(raw.primaryColor), imageUrl: str(raw.imageUrl) };
    tenantName.value = str(data?.tenantName);
  } catch {
    /* fall back to password-only, on the suite's default screen */
  }
});

const localeLabels: Record<string, string> = {
  de: 'Deutsch', en: 'English', fr: 'Français', es: 'Español',
  nl: 'Nederlands', pl: 'Polski', sv: 'Svenska', it: 'Italiano', pt: 'Português',
};
const localeOptions = SUPPORTED_LOCALES.map((code) => ({
  code,
  label: localeLabels[code] || code.toUpperCase(),
}));

function onLocaleSelect(value: string | number | undefined) {
  if (value) setLocale(String(value));
}

// Signing in is a user-initiated write, so it belongs in the seam with a tier —
// the store cannot hold it (no setup context) and a blanket transport exemption on
// `stores/auth.ts` would silently swallow a failed sign-in.
//
// TIER `inlineEdit`, deliberately, and not the `explicitSave` a submit button
// usually earns: what confirms a sign-in is the workspace the user lands on, and on
// the module-handoff path (`postLoginRedirectUrl`) the success is a full-page
// cross-origin navigation where a toast is provably never rendered.
//
// CHANNEL `inPlace`: the credentials are still on screen and the alert box above
// the form renders `formError`. It replaces the old `e.message` read — the raw
// backend/axios string was neither localized nor ours to show (decision D6).
const { pending: loading, formError, reset: resetLoginError, mutate: signIn } = useApiMutation({
  tier: 'inlineEdit',
  errorChannel: 'inPlace',
  run: async () => {
    // On a per-tenant host, the server requires membership of that tenant and
    // returns the same "Invalid credentials" as a bad account when it's missing,
    // so a non-member never gets in past the login form. Empty on the apex host.
    const tenantSlug = tenantSlugFromWorkspaceHost();
    const { data } = await userApi.post('/auth/login', {
      email: email.value,
      password: password.value,
      tenantSlug,
    });
    // A system admin's password answers a challenge, not a session (issue 619).
    if (!isMfaChallenge(data)) authStore.adoptSession(data);
    return data;
  },
});

// ── The second factor of a system admin (issue 619, ADR 0094) ───────────────
type MfaChallenge = { mfaRequired: true; mfaToken: string; mfaEnrollment: boolean };
function isMfaChallenge(data: unknown): data is MfaChallenge {
  const value = data as Partial<MfaChallenge> | null;
  return !!value && value.mfaRequired === true && typeof value.mfaToken === 'string';
}
const mfaChallenge = ref<MfaChallenge | null>(null);
const mfaCode = ref('');

function leaveMfa(message: string) {
  mfaChallenge.value = null;
  mfaCode.value = '';
  password.value = '';
  error.value = message;
  void startConditionalPasskey();
}

// CONSUMER (`errorChannel: 'handled'`): `submitMfaCode` renders the refusal in
// the alert box above, and an expired challenge goes back to the password.
const { pending: mfaPending, mutate: postMfaCode } = useApiMutation({
  tier: 'inlineEdit',
  errorChannel: 'handled',
  run: async (mfaToken: string, code: string) => {
    const { data } = await userApi.post('/auth/mfa/verify', { mfaToken, code });
    return data;
  },
});

async function submitMfaCode() {
  const challenge = mfaChallenge.value;
  if (!challenge) return;
  error.value = '';
  const raw = mfaCode.value.trim();
  const outcome = await postMfaCode(challenge.mfaToken, /^[\d\s]+$/.test(raw) ? raw.replace(/\s/g, '') : raw);
  mfaCode.value = '';
  if (outcome.ok) {
    await completeSignIn(outcome.data);
    return;
  }
  if (outcome.code === 'invalid_mfa_code') error.value = t('login.mfaInvalid');
  else if (outcome.code === 'mfa_code_reused') error.value = t('login.mfaReused');
  else if (outcome.code && outcome.code.startsWith('mfa_')) leaveMfa(t('login.mfaExpired'));
  else error.value = outcome.formError;
}

// One place both ceremonies land, so a passkey session is adopted and routed
// exactly like a password one — the token pair is the same pair.
async function completeSignIn(data: unknown) {
  authStore.adoptSession(data as Parameters<typeof authStore.adoptSession>[0]);
  unmounted = true;
  stopConditionalPasskey();
  const dest = postLoginRedirectUrl(route.query.module, route.query.returnPath);
  if (dest) {
    window.location.href = dest;
    return;
  }
  // #445 M2 — a workspace-internal deep link (no module) the requiresAuth gate
  // bounced here from; come back to it instead of the homebase.
  router.push(internalReturnPath(route.query.module, route.query.returnPath) ?? '/');
}

// The button. `explicitSave` would be wrong — the successful outcome is a whole
// new page, and the failure is what needs saying, in place, next to the button.
//
// The whole ceremony lives inside `run`, both halves of it: a helper holding the
// HTTP would be a write the seam cannot see, and this is a sign-in.
const { mutate: runPasskeySignIn } = useApiMutation({
  tier: 'inlineEdit',
  errorChannel: 'inPlace',
  run: async () => {
    // The autofill ceremony holds the browser's single WebAuthn slot; release
    // it before asking for the modal prompt, or the prompt never opens.
    stopConditionalPasskey();
    const { data: options } = await userApi.post('/auth/passkey/options', {});
    let assertion;
    try {
      assertion = await getPasskeyAssertion(options);
    } catch (error) {
      // A dismissed prompt is the browser's outcome, not a failure of ours — it
      // must not reach the API-error seam, which would log it as a bug. The form
      // is still open, so the field gets its autofill ceremony back.
      if (ceremonyOutcome(error) !== 'dismissed') throw error;
      void startConditionalPasskey();
      return;
    }
    const { data } = await userApi.post('/auth/passkey/verify', {
      response: assertion,
      challenge: options.challenge,
      tenantSlug: tenantSlugFromWorkspaceHost(),
    });
    await completeSignIn(data);
  },
  onError: (outcome) => {
    // Only a refusal from OUR server is reported; the browser's own outcomes were
    // handled inside `run`, and anything else that lands here is a bug the seam
    // has already logged.
    passkeyError.value = outcome.status ? t('login.passkeyFailed') : '';
    // Either way the form is still open, so the field gets its autofill back.
    void startConditionalPasskey();
  },
});

// CONSUMER (`errorChannel: 'handled'`): nothing consumes a failure here on
// purpose. This is the AUTOFILL ceremony — the browser offers the passkey inside
// the e-mail field, and it ends by being aborted far more often than it ends in a
// sign-in, because the person typed a password instead. That abort is the normal
// end of it. Reporting it would put an error on a login screen for something the
// person did not do; the button above and the password form are both still there.
const { mutate: runConditionalPasskey } = useApiMutation({
  tier: 'inlineEdit',
  errorChannel: 'handled',
  run: async () => {
    // The controller exists BEFORE the first await: a `stopConditionalPasskey`
    // that lands while the options are in flight (a fast autofilled password
    // login, leaving the page) must find something to abort. Created after the
    // await, that stop aborted nothing and the ceremony then started anyway —
    // on a page nobody was left to call it off from, holding the browser's one
    // WebAuthn slot against every later ceremony in this tab.
    const controller = new AbortController();
    conditionalAbort = controller;
    const { data: options } = await userApi.post('/auth/passkey/options', {});
    if (controller.signal.aborted) return;
    let assertion;
    try {
      assertion = await getPasskeyAssertion(options, { conditional: true, signal: controller.signal });
    } catch (error) {
      // The abort is how this ceremony normally ENDS — on every password login,
      // on every button press. It is not a failure and must not be logged as one.
      // The slot is released only if this run still holds it: by the time an
      // aborted ceremony gets here, `stopConditionalPasskey` may already have
      // handed the slot to the next one.
      if (ceremonyOutcome(error) !== 'dismissed') throw error;
      if (conditionalAbort === controller) stopConditionalPasskey();
      return;
    }
    const { data } = await userApi.post('/auth/passkey/verify', {
      response: assertion,
      challenge: options.challenge,
      tenantSlug: tenantSlugFromWorkspaceHost(),
    });
    await completeSignIn(data);
  },
  onError: (outcome) => {
    // Whatever failed, this ceremony is over and it was the one holding the
    // slot (a run only starts once the previous one has released it), so the
    // slot is released here. The abort and a dismissed prompt never reach this
    // — they are handled in `run`.
    stopConditionalPasskey();
    // The one failure worth acting on: the challenge behind the autofill prompt
    // has a few minutes' life, and a login tab left open outlives it. When the
    // person finally picks their passkey the server says "expired" — so ask for
    // a fresh challenge and offer the field again, rather than leaving a prompt
    // that can only ever fail. Any other refusal stays silent: the button and
    // the password form are still on screen.
    if (outcome.status === 400 && outcome.code === 'challenge_expired') void startConditionalPasskey();
  },
});

function stopConditionalPasskey() {
  conditionalAbort?.abort();
  conditionalAbort = null;
  conditionalActive = false;
}

async function signInWithPasskey() {
  passkeyError.value = '';
  passkeyPending.value = true;
  try {
    await runPasskeySignIn();
  } finally {
    passkeyPending.value = false;
  }
}

// The autofill variant: the browser offers the passkey inside the e-mail field,
// so the fastest path needs no button at all. It runs for as long as the form is
// open and is aborted the moment anything else signs in — an aborted conditional
// ceremony is the NORMAL end of it, which is why nothing here reports an error.
async function startConditionalPasskey() {
  if (unmounted || conditionalActive) return;
  if (!(await conditionalMediationAvailable())) return;
  if (unmounted || conditionalActive) return;
  conditionalActive = true;
  // Cleared by `stopConditionalPasskey` or the ceremony's own onError — not
  // here, because onError may already have started the NEXT ceremony.
  await runConditionalPasskey();
}

onBeforeUnmount(() => {
  unmounted = true;
  stopConditionalPasskey();
});

async function handleLogin() {
  error.value = '';
  resetLoginError();
  stopConditionalPasskey();
  const outcome = await signIn();
  // A refused password leaves the form open, so the field gets its passkey
  // autofill back.
  if (!outcome.ok) { void startConditionalPasskey(); return; }
  if (isMfaChallenge(outcome.data)) {
    // An operator with no authenticator yet sets one up on the console, which is
    // the only place that shows a QR code; this host only ever asks for a code.
    if (outcome.data.mfaEnrollment) {
      password.value = '';
      error.value = t('login.mfaEnrolOnConsole');
      void startConditionalPasskey();
      return;
    }
    mfaChallenge.value = outcome.data;
    return;
  }
  // If a module sent us here to log in, hand the fresh session back to it
  // (full-page nav — it's a sibling origin); otherwise land on the homebase.
  const dest = postLoginRedirectUrl(route.query.module, route.query.returnPath);
  if (dest) {
    window.location.href = dest;
    return;
  }
  // #445 M2 — a workspace-internal deep link (no module) the requiresAuth gate
  // bounced here from; come back to it instead of the homebase.
  router.push(internalReturnPath(route.query.module, route.query.returnPath) ?? '/');
}
</script>

<style scoped>
/* #364: the sign-in card sits over the tenant's own hero image, so it reads better
   letting a little of that picture through than as an opaque slab. Derived from the
   card surface token with `color-mix` rather than a literal rgba, so it still follows
   the theme (and the dark-mode surface) instead of pinning one colour. The blur is
   what keeps the form legible over a photo nobody here has seen — translucency alone
   would put arbitrary detail behind the labels. */
.login-card {
  /* The padding lives here, not in a `p-*` utility: `Card` renders
     `lumi-card--pad-none` from an unscoped component stylesheet that is loaded
     after the utilities, so a same-specificity `p-10` on the element loses and
     the form ends up flush with the card edge. The scoped selector outranks it. */
  padding: var(--space-8);
}
/* The Lumi mark wears the card's surface (#465), so the screen's two panes
   read as one set. */
.login-card,
.login-mark {
  background: color-mix(in srgb, var(--surface-card) 60%, transparent);
  backdrop-filter: blur(10px);
}
.login-mark {
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
}

/* The shared Input's own focus ring is the suite's ink (tokens/base.css); on THIS
   screen the field has to answer to the tenant's own accent instead, the same
   inline override `brandStyle` gives the button and the links above. `:deep`
   because Input is an unscoped component stylesheet. */
.login-field :deep(.login-input:focus) {
  border-color: var(--accent);
  box-shadow: 0 0 0 2px var(--accent-ring);
}

/* The language switcher stays the small inline control it always was — `Select`
   defaults to full width, which would stretch it across the card. */
.login-locale {
  width: auto;
  flex-shrink: 0;
}
</style>
